import {
    WorkspaceMember,
    WorkspaceMemberEntityAssignment,
} from "../types/workspace";
import {
    UserWorkspaceRole,
    userWorkspaceRolesInOrder,
} from "../userWorkspaceRoles";
import { InviteCode } from "../types/inviteCode";

export const CAN_MANAGE_MEMBERS: UserWorkspaceRole[] = [
    UserWorkspaceRole.ACCOUNTANT,
    UserWorkspaceRole.ADMIN,
    UserWorkspaceRole.PRIMARY_ADMIN,
] as const;

export function validateInviteMember(
    actingMember: WorkspaceMember,
    inviteWorkspaceRole: UserWorkspaceRole,
    inviteEntityAssignments: WorkspaceMemberEntityAssignment[],
) {
    return (
        validateManageWorkspaceMemberRole(actingMember, inviteWorkspaceRole) ||
        validateManageEntityMembership(actingMember, inviteEntityAssignments)
    );
}

export function validateResendInvite(actingMember: WorkspaceMember) {
    if (!CAN_MANAGE_MEMBERS.includes(actingMember.role)) {
        return "Cannot resend invite";
    }

    return null;
}

export function validateCancelInvite(
    actingMember: WorkspaceMember,
    invite: InviteCode,
) {
    if (!invite.role) {
        return "Not a member invite";
    }

    return (
        validateManageWorkspaceMemberRole(actingMember, invite.role) ??
        validateManageEntityMembership(actingMember, invite.entityAssignments)
    );
}

export function validateManageWorkspaceAssignment(
    actingMember: WorkspaceMember,
    managedMember: WorkspaceMember,
) {
    if (actingMember.user.id === managedMember.user.id) {
        return "Cannot update your own assignment";
    }

    return (
        validateManageWorkspaceMemberRole(actingMember, managedMember.role) ??
        validateManageEntityMembership(
            actingMember,
            managedMember.entityAssignments,
        )
    );
}

function validateManageWorkspaceMemberRole(
    actingMember: WorkspaceMember,
    managedMemberRole: UserWorkspaceRole,
): string | null {
    if (!CAN_MANAGE_MEMBERS.includes(actingMember.role)) {
        return "Cannot manage members";
    }

    if (
        userWorkspaceRolesInOrder.indexOf(managedMemberRole) <
        userWorkspaceRolesInOrder.indexOf(actingMember.role)
    ) {
        return "Cannot manage members with a higher role";
    }

    return null;
}

function validateManageEntityMembership(
    actingMember: WorkspaceMember,
    entityAssignments: WorkspaceMemberEntityAssignment[],
): string | null {
    // ignore entity requirements if the acting member is a primary admin
    if (actingMember.role === UserWorkspaceRole.PRIMARY_ADMIN) {
        return null;
    }

    const actingUserEntities = new Set(
        actingMember.entityAssignments.map((ea) => ea.entity.id),
    );

    const hasAccessToAllEntities = entityAssignments.every((ea) =>
        actingUserEntities.has(ea.entity.id),
    );

    if (!hasAccessToAllEntities) {
        return "Cannot manage members with access to entities you don't have access to";
    }

    return null;
}
